import type { AssetListItem } from '@cartwhl/common/api/getAssets';
import type { PaginatedAsyncState } from '@cartwhl/common/types';

import type { AssetLibraryView } from './assetLibraryView';

export type AssetLibraryAssetsCacheKey = 'main' | 'trash' | 'bookmarks';

export type AssetLibraryAssetsLoadState = {
  libraryView: AssetLibraryView;
  mainAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
  trashAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
  bookmarksAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
};

export const ASSET_LIBRARY_ASSETS_CACHE_KEYS = [
  'main',
  'trash',
  'bookmarks',
] as const satisfies readonly AssetLibraryAssetsCacheKey[];

export const ASSET_LIBRARY_CACHE_LOAD_KEYS = {
  main: 'mainAssetsLoad',
  trash: 'trashAssetsLoad',
  bookmarks: 'bookmarksAssetsLoad',
} as const satisfies Record<
  AssetLibraryAssetsCacheKey,
  keyof AssetLibraryAssetsLoadState
>;

export const INITIAL_ASSET_LIBRARY_ASSETS_LOAD: PaginatedAsyncState<
  AssetListItem[]
> = { status: 'initial', data: undefined };

export function getAssetsCacheKey(
  libraryView: AssetLibraryView,
): AssetLibraryAssetsCacheKey {
  if (libraryView === 'trash') {
    return 'trash';
  }

  if (libraryView === 'bookmarks') {
    return 'bookmarks';
  }

  return 'main';
}

export function getAssetsLoadForCache(
  state: AssetLibraryAssetsLoadState,
  cacheKey: AssetLibraryAssetsCacheKey,
): PaginatedAsyncState<AssetListItem[]> {
  return state[ASSET_LIBRARY_CACHE_LOAD_KEYS[cacheKey]];
}

export function getActiveAssetsLoad(
  state: AssetLibraryAssetsLoadState,
): PaginatedAsyncState<AssetListItem[]> {
  return getAssetsLoadForCache(state, getAssetsCacheKey(state.libraryView));
}

/** Loaded main-library items; used to resolve bookmark state for stacked groups. */
export function getMainLibraryAssets(
  state: AssetLibraryAssetsLoadState,
): readonly AssetListItem[] {
  return getAssetsLoadForCache(state, 'main').data?.items ?? [];
}

/** Items from the cache used to resolve bookmark icon state for the active view. */
export function getBookmarkReferenceAssets(
  state: AssetLibraryAssetsLoadState,
): readonly AssetListItem[] {
  if (state.libraryView === 'bookmarks') {
    return getActiveAssetsLoad(state).data?.items ?? [];
  }

  return getMainLibraryAssets(state);
}
